import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Order } from '../../types'
import { localDb, formatCurrency } from '../../lib/db'

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalOrders: 0,
    pendingOrders: 0,
    totalRevenue: 0,
    lowStock: 0
  })
  const [recentOrders, setRecentOrders] = useState<Order[]>([])

  useEffect(() => {
    // Calculate stats
    const products = localDb.products.getAll()
    const orders = localDb.orders.getAll()

    const totalRevenue = orders
      .filter(o => o.status === 'completed' || o.status === 'paid' || o.status === 'ready')
      .reduce((sum, o) => sum + o.total, 0)

    const pendingOrders = orders.filter(o => o.status === 'pending' || o.status === 'paid').length
    const lowStock = products.filter(p => p.stock <= 5).length

    setStats({
      totalProducts: products.length,
      totalOrders: orders.length,
      pendingOrders,
      totalRevenue,
      lowStock
    })

    // Recent orders
    setRecentOrders(
      orders
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 5)
    )
  }, [])

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusColor = (status: Order['status']) => {
    const colors: Record<Order['status'], string> = {
      pending: 'badge-warning',
      paid: 'badge-primary',
      ready: 'badge-success',
      completed: 'badge-success',
      cancelled: 'badge-error'
    }
    return colors[status]
  }

  return (
    <div>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Dashboard</h1>
        <p className="admin-page-subtitle">Welcome back! Here's your store overview.</p>
      </div>

      {/* Stats Grid */}
      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <p className="admin-stat-label">Total Products</p>
          <p className="admin-stat-value">{stats.totalProducts}</p>
          <Link to="/admin/inventory" className="admin-stat-link">
            View Inventory →
          </Link>
        </div>

        <div className="admin-stat-card">
          <p className="admin-stat-label">Total Orders</p>
          <p className="admin-stat-value">{stats.totalOrders}</p>
          <Link to="/admin/orders" className="admin-stat-link">
            View Orders →
          </Link>
        </div>

        <div className="admin-stat-card" style={{ borderColor: stats.pendingOrders > 0 ? 'var(--color-warning)' : undefined }}>
          <p className="admin-stat-label">Pending Orders</p>
          <p className="admin-stat-value" style={{ color: stats.pendingOrders > 0 ? 'var(--color-warning)' : undefined }}>
            {stats.pendingOrders}
          </p>
          {stats.pendingOrders > 0 && (
            <span className="admin-stat-alert">Needs attention</span>
          )}
        </div>

        <div className="admin-stat-card">
          <p className="admin-stat-label">Total Revenue</p>
          <p className="admin-stat-value text-gradient">{formatCurrency(stats.totalRevenue)}</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h2>Quick Actions</h2>
        <div className="quick-actions-grid">
          <Link to="/admin/pos" className="quick-action-card">
            <span className="quick-action-icon">🛒</span>
            <span className="quick-action-label">Open POS</span>
          </Link>
          <Link to="/admin/inventory" className="quick-action-card">
            <span className="quick-action-icon">📦</span>
            <span className="quick-action-label">Add Product</span>
          </Link>
          <Link to="/admin/ai-studio" className="quick-action-card">
            <span className="quick-action-icon">✨</span>
            <span className="quick-action-label">AI Studio</span>
          </Link>
          <Link to="/admin/orders" className="quick-action-card">
            <span className="quick-action-icon">📋</span>
            <span className="quick-action-label">Manage Orders</span>
          </Link>
        </div>
      </div>

      {/* Recent Orders */}
      <div className="recent-section">
        <div className="section-header">
          <h2>Recent Orders</h2>
          <Link to="/admin/orders" className="view-all-link">View All →</Link>
        </div>

        {recentOrders.length === 0 ? (
          <div className="empty-section">
            <p>No orders yet</p>
          </div>
        ) : (
          <div className="recent-orders-table">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Date</th>
                  <th>Pickup Code</th>
                  <th>Total</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map(order => (
                  <tr key={order.id}>
                    <td className="order-id-cell">#{order.id.slice(0, 8).toUpperCase()}</td>
                    <td>{formatDate(order.created_at)}</td>
                    <td><code className="pickup-code">{order.pickup_code}</code></td>
                    <td className="amount-cell">{formatCurrency(order.total)}</td>
                    <td><span className={`badge ${getStatusColor(order.status)}`}>{order.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Low Stock Alert */}
      {stats.lowStock > 0 && (
        <div className="alert-section">
          <div className="alert alert-warning">
            <span className="alert-icon">⚠️</span>
            <div>
              <strong>{stats.lowStock} product(s) with low stock</strong>
              <p>Some items need restocking soon.</p>
            </div>
            <Link to="/admin/inventory" className="btn btn-sm btn-secondary">
              View
            </Link>
          </div>
        </div>
      )}

      <style>{`
        .admin-stat-link {
          display: inline-block;
          margin-top: var(--space-2);
          font-size: var(--text-sm);
          color: var(--color-primary-light);
        }
        
        .admin-stat-alert {
          display: inline-block;
          margin-top: var(--space-2);
          font-size: var(--text-xs);
          color: var(--color-warning);
        }
        
        .quick-actions {
          margin-bottom: var(--space-8);
        }
        
        .quick-actions h2 {
          font-size: var(--text-lg);
          margin-bottom: var(--space-4);
        }
        
        .quick-actions-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: var(--space-4);
        }
        
        .quick-action-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: var(--space-2);
          padding: var(--space-6);
          background: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-xl);
          text-decoration: none;
          color: var(--text-primary);
          transition: all var(--transition-fast);
        }
        
        .quick-action-card:hover {
          border-color: var(--color-primary);
          transform: translateY(-2px);
        }
        
        .quick-action-icon {
          font-size: 2rem;
        }
        
        .quick-action-label {
          font-weight: 500;
          font-size: var(--text-sm);
        }
        
        .recent-section {
          margin-bottom: var(--space-8);
        }
        
        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: var(--space-4);
        }
        
        .section-header h2 {
          font-size: var(--text-lg);
        }
        
        .view-all-link {
          font-size: var(--text-sm);
          color: var(--color-primary-light);
        }
        
        .empty-section {
          padding: var(--space-8);
          text-align: center;
          color: var(--text-muted);
          background: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-xl);
        }
        
        .recent-orders-table {
          background: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-xl);
          overflow: hidden;
        }
        
        .order-id-cell {
          font-family: monospace;
        }
        
        .pickup-code {
          padding: var(--space-1) var(--space-2);
          background: var(--bg-tertiary);
          border-radius: var(--radius-sm);
          font-family: monospace;
          font-weight: 600;
        }
        
        .amount-cell {
          font-weight: 600;
        }
        
        .alert-section {
          margin-bottom: var(--space-8);
        }
        
        .alert {
          display: flex;
          align-items: center;
          gap: var(--space-4);
          padding: var(--space-4);
          border-radius: var(--radius-xl);
        }
        
        .alert-warning {
          background: rgba(245, 158, 11, 0.1);
          border: 1px solid rgba(245, 158, 11, 0.3);
        }
        
        .alert-icon {
          font-size: 1.5rem;
        }
        
        .alert div {
          flex: 1;
        }
        
        .alert p {
          font-size: var(--text-sm);
          color: var(--text-muted);
          margin-top: var(--space-1);
        }
        
        @media (max-width: 768px) {
          .quick-actions-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
      `}</style>
    </div>
  )
}
